import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../../services/api';

const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/.test(password)) {
      setError('Password must contain uppercase, lowercase, and a number');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await api.resetPassword(token, password);
      toast({
        title: 'Password Reset Successful',
        description: 'You can now log in with your new password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
      toast({
        title: 'Error',
        description: err.message || 'Failed to reset password',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="white" display="flex" alignItems="center" py={8}>
      <Container maxW="420px" w="full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="12px"
            p={8}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.05)"
          >
            <VStack spacing={6} align="stretch">
              <VStack spacing={2} align="start">
                <Heading as="h1" fontSize="28px" fontWeight="700" color="neutral.900">
                  Set New Password
                </Heading>
                <Text fontSize="14px" color="neutral.600" lineHeight="1.5">
                  Enter your new password below.
                </Text>
              </VStack>

              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired isInvalid={!!error && error.includes('Password')}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      New Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Confirm New Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {error && (
                      <FormErrorMessage fontSize="12px">{error}</FormErrorMessage>
                    )}
                  </FormControl>

                  <Button
                    type="submit"
                    w="full"
                    h="40px"
                    bg="primary.500"
                    color="white"
                    fontWeight="600"
                    fontSize="14px"
                    borderRadius="8px"
                    _hover={{
                      bg: 'primary.600',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(26, 94, 63, 0.3)',
                    }}
                    _active={{
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(26, 94, 63, 0.2)',
                    }}
                    isLoading={isLoading}
                    mt={2}
                  >
                    Reset Password
                  </Button>
                </VStack>
              </form>
            </VStack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ResetPassword;
